import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore, type SessionUser } from './auth.js';

export interface OrgSummary {
  id: string;
  role: SessionUser['memberships'][number]['role'];
}

/**
 * Devuelve la primera membership del usuario autenticado, o null si todavía
 * no creó organización. La UI lo usa para decidir entre mostrar el dashboard
 * normal o redirigir al flujo de onboarding (crear org).
 */
export function useCurrentOrg(): OrgSummary | null {
  const user = useAuthStore((s) => s.user);
  const m = user?.memberships?.[0];
  if (!m) return null;
  return { id: m.orgId, role: m.role };
}

export interface OrgCreditsResponse {
  balance: number;
  freeMonthlyAllowance: number;
  freeMonthlyConsumed: number;
  freeMonthlyResetAt: string;
  ledger: Array<{
    id: string;
    type: 'purchase' | 'consume' | 'refund' | 'free_monthly' | 'referral' | 'adjustment';
    amount: number;
    balanceAfter: number;
    createdAt: string;
    notes: string | null;
  }>;
}

import { api } from './api.js';

export function useOrgCredits(orgId: string | null) {
  return useQuery<OrgCreditsResponse>({
    queryKey: ['orgs', orgId, 'credits'],
    queryFn: () => api<OrgCreditsResponse>(`/orgs/${orgId}/credits`),
    enabled: Boolean(orgId),
    refetchOnWindowFocus: false,
  });
}

/**
 * Listado de paquetes de créditos *localizados* a una moneda objetivo.
 * El cargo real lo hace Stripe Adaptive Pricing en checkout, así que las
 * cifras aquí son aproximadas (de ahí `isEstimate`). Hay dos consumidores:
 *   - Dashboard autenticado: `useOrgCreditPackages(orgId)` → moneda derivada
 *     del país de la org.
 *   - Landing público: `usePublicCreditPackages()` → moneda derivada del
 *     CF-IPCountry del visitante, sin necesidad de sesión.
 */
export interface LocalizedCreditPackage {
  id: string;
  credits: number;
  totalCents: number;
  perUnitCents: number;
  totalUsdCents: number;
  perUnitUsdCents: number;
}

export interface OrgCreditPackagesResponse {
  referenceCurrency: 'USD';
  displayCurrency: string;
  isEstimate: boolean;
  fxRate: number;
  fxAsOf: string;
  fxSource: 'live' | 'cache' | 'static_fallback';
  packages: LocalizedCreditPackage[];
}

export function useOrgCreditPackages(orgId: string | null) {
  return useQuery<OrgCreditPackagesResponse>({
    queryKey: ['orgs', orgId, 'credits', 'packages'],
    queryFn: () => api<OrgCreditPackagesResponse>(`/orgs/${orgId}/credits/packages`),
    enabled: Boolean(orgId),
    refetchOnWindowFocus: false,
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * Variante pública del listado de paquetes para el landing. La API
 * resuelve la moneda desde la IP del visitante (ipinfo.io primary +
 * geoip-lite fallback, cacheado en Redis). Misma forma de respuesta
 * que la versión autenticada para reusar el renderer.
 */
export function usePublicCreditPackages() {
  return useQuery<OrgCreditPackagesResponse>({
    queryKey: ['public', 'credits', 'packages'],
    queryFn: () => api<OrgCreditPackagesResponse>(`/orgs/credits/packages`),
    refetchOnWindowFocus: false,
    staleTime: 60 * 60 * 1000,
  });
}

export interface OrgEvent {
  id: string;
  slug: string;
  status: 'draft' | 'live' | 'ended' | 'cancelled';
  startsAt: string;
  endsAt: string;
  timezone: string;
  defaultLocale: 'es' | 'en';
  translations: Record<string, { title: string; description?: string; venueLabel?: string }>;
  currency: string;
  bannerUrl: string | null;
}

export function useOrgEvents(orgId: string | null) {
  return useQuery<{ events: OrgEvent[] }>({
    queryKey: ['orgs', orgId, 'events'],
    queryFn: () => api<{ events: OrgEvent[] }>(`/events?orgId=${encodeURIComponent(orgId ?? '')}`),
    enabled: Boolean(orgId),
    refetchOnWindowFocus: false,
  });
}

export interface OrgDetails {
  id: string;
  name: string;
  slug: string;
  country: string;
  stripeAccountId: string | null;
  stripeOnboardingComplete: boolean;
  reviewStatus: 'pending' | 'approved' | 'rejected';
  creditsBalance: number;
  freeMonthlyAllowance: number;
  freeMonthlyConsumed: number;
  settings: {
    defaultLocale: 'es' | 'en';
    defaultCurrency: string;
    defaultServiceFeePct: number;
    brandColor?: string;
    logoUrl?: string;
  };
}

/**
 * Detalle completo de la org del owner — incluye `stripeOnboardingComplete`.
 * La UI lo usa para decidir si mostrar el banner "conectá Stripe" o no.
 */
export function useOrgDetails(orgId: string | null) {
  return useQuery<OrgDetails>({
    queryKey: ['orgs', orgId, 'details'],
    queryFn: () => api<OrgDetails>(`/orgs/${orgId}`),
    enabled: Boolean(orgId),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}

export interface StripeRefreshResponse {
  stripeAccountId: string | null;
  stripeOnboardingComplete: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  pendingVerification: boolean;
}

/**
 * Pull autoritario del estado de Stripe Connect para la org. Lo llamamos
 * cuando el webhook `account.updated` puede no haber llegado todavía:
 *   - Después de volver de Stripe Hosted Onboarding (`?stripe=ok`).
 *   - Al abrir un evento draft que dice "conectá Stripe" (re-check inline).
 *
 * En el success invalidamos los caches que dependen del estado: la propia
 * org y cualquier evento abierto.
 */
export function useStripeRefresh(orgId: string | null) {
  const queryClient = useQueryClient();
  return useMutation<StripeRefreshResponse, Error, void>({
    mutationFn: () =>
      api<StripeRefreshResponse>(`/orgs/${orgId}/stripe/refresh`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgs', orgId, 'details'] });
      queryClient.invalidateQueries({ queryKey: ['event'] });
    },
  });
}
